import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Token refresh buffer - refresh 5 minutes before expiry
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

const AuthContext = createContext({
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  login: async () => { },
  register: async () => { },
  logout: () => { },
  refreshUser: async () => { },
  refreshToken: async () => { },
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("foundry-token"));
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Schedule token refresh before expiry
  const scheduleTokenRefresh = useCallback((expiresAt) => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (!expiresAt) return;

    const expiresAtMs = expiresAt * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = expiresAtMs - now;
    const refreshTime = timeUntilExpiry - REFRESH_BUFFER_MS;

    if (refreshTime > 0) {
      console.log(`[Auth] Token refresh scheduled in ${Math.round(refreshTime / 60000)} minutes`);
      refreshTimeoutRef.current = setTimeout(async () => {
        console.log("[Auth] Auto-refreshing token...");
        await refreshTokenFn();
      }, refreshTime);
    } else if (timeUntilExpiry > 0) {
      // Token is about to expire, refresh immediately
      console.log("[Auth] Token expiring soon, refreshing now...");
      refreshTokenFn();
    }
  }, []);

  // Refresh token function
  const refreshTokenFn = useCallback(async () => {
    if (isRefreshingRef.current) {
      console.log("[Auth] Already refreshing, skipping...");
      return null;
    }

    const storedRefreshToken = localStorage.getItem("foundry-refresh-token");
    if (!storedRefreshToken) {
      console.log("[Auth] No refresh token available");
      return null;
    }

    isRefreshingRef.current = true;

    try {
      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: storedRefreshToken,
      });

      if (response.data?.access_token) {
        localStorage.setItem("foundry-token", response.data.access_token);
        localStorage.setItem("foundry-refresh-token", response.data.refresh_token);
        if (response.data.expires_at) {
          localStorage.setItem("foundry-expires-at", response.data.expires_at.toString());
        }
        setToken(response.data.access_token);
        if (response.data.user) {
          setUser(response.data.user);
        }

        // Schedule next refresh
        if (response.data.expires_at) {
          scheduleTokenRefresh(response.data.expires_at);
        }

        console.log("[Auth] Token refreshed successfully");
        return response.data.access_token;
      }
    } catch (error) {
      console.error("[Auth] Token refresh failed:", error);
      // Only logout if refresh token is invalid (not network errors)
      if (error.response?.status === 401) {
        console.log("[Auth] Refresh token invalid, logging out");
        localStorage.removeItem("foundry-token");
        localStorage.removeItem("foundry-refresh-token");
        localStorage.removeItem("foundry-expires-at");
        setToken(null);
        setUser(null);
      }
    } finally {
      isRefreshingRef.current = false;
    }
    return null;
  }, [scheduleTokenRefresh]);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("foundry-token");
      const storedRefreshToken = localStorage.getItem("foundry-refresh-token");
      const storedExpiresAt = localStorage.getItem("foundry-expires-at");

      if (storedToken) {
        try {
          // Check if token might be expired
          const expiresAt = storedExpiresAt ? parseInt(storedExpiresAt) : null;
          const isExpired = expiresAt && (expiresAt * 1000) < Date.now();

          // If token is expired but we have a refresh token, try to refresh first
          if (isExpired && storedRefreshToken) {
            console.log("[Auth] Token expired, attempting refresh...");
            const newToken = await refreshTokenFn();
            if (!newToken) {
              throw new Error('Token refresh failed');
            }
            setLoading(false);
            return;
          }

          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });

          // Validate response structure
          if (!response.data || !response.data.user) {
            throw new Error('Invalid response structure from server');
          }

          setUser(response.data.user);

          // Update token if server provided a new one
          if (response.data.access_token) {
            localStorage.setItem("foundry-token", response.data.access_token);
            setToken(response.data.access_token);
          } else {
            setToken(storedToken);
          }

          // Schedule refresh based on stored expiry
          if (expiresAt) {
            scheduleTokenRefresh(expiresAt);
          }
        } catch (error) {
          console.error("Token validation failed:", error);

          // Try to refresh if we have a refresh token
          if (storedRefreshToken) {
            console.log("[Auth] Trying to refresh token...");
            const newToken = await refreshTokenFn();
            if (newToken) {
              setLoading(false);
              return;
            }
          }

          localStorage.removeItem("foundry-token");
          localStorage.removeItem("foundry-refresh-token");
          localStorage.removeItem("foundry-expires-at");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [refreshTokenFn, scheduleTokenRefresh]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });

    // Validate response structure
    if (!response.data || !response.data.access_token || !response.data.user) {
      throw new Error('Invalid response from server. Please try again.');
    }

    const { access_token, refresh_token, expires_at, user: userData } = response.data;
    localStorage.setItem("foundry-token", access_token);
    if (refresh_token) {
      localStorage.setItem("foundry-refresh-token", refresh_token);
    }
    if (expires_at) {
      localStorage.setItem("foundry-expires-at", expires_at.toString());
      scheduleTokenRefresh(expires_at);
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = "operator") => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });

    // Validate response structure
    if (!response.data || !response.data.access_token || !response.data.user) {
      throw new Error('Invalid response from server. Please try again.');
    }

    const { access_token, refresh_token, expires_at, user: userData } = response.data;
    localStorage.setItem("foundry-token", access_token);
    if (refresh_token) {
      localStorage.setItem("foundry-refresh-token", refresh_token);
    }
    if (expires_at) {
      localStorage.setItem("foundry-expires-at", expires_at.toString());
      scheduleTokenRefresh(expires_at);
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    // Clear refresh timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    // Best-effort tell the server so the device session is marked as logged out.
    // Fire-and-forget — don't block local logout on network state.
    const storedToken = localStorage.getItem("foundry-token");
    if (storedToken) {
      axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${storedToken}` },
      }).catch(() => {});
    }
    localStorage.removeItem("foundry-token");
    localStorage.removeItem("foundry-refresh-token");
    localStorage.removeItem("foundry-expires-at");
    setToken(null);
    setUser(null);
  };

  // Refresh user data from server (useful after permission updates)
  const refreshUser = async () => {
    const storedToken = localStorage.getItem("foundry-token");
    if (!storedToken) return null;

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
      });

      if (response.data?.user) {
        setUser(response.data.user);
        return response.data.user;
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
    return null;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        refreshToken: refreshTokenFn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Axios interceptor for auth
export const createAuthAxios = (token) => {
  const instance = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return instance;
};

// Setup global axios interceptor for automatic token refresh on 401
let isRefreshingGlobal = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Setup interceptor
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Device revoked by an admin — wipe session and bounce to login (no refresh attempt).
    if (error.response?.status === 401 && error.response?.data?.error === 'device_revoked') {
      localStorage.removeItem("foundry-token");
      localStorage.removeItem("foundry-refresh-token");
      localStorage.removeItem("foundry-expires-at");
      window.location.href = '/login?revoked=1';
      return new Promise(() => {});
    }

    // Maintenance mode: route to SPA maintenance page (preserves React state)
    if (error.response?.status === 503 && error.response?.data?.error === 'maintenance') {
      if (window.location.pathname !== '/maintenance' && window.location.pathname !== '/login') {
        window.history.pushState({}, '', '/maintenance');
        // Force React Router to pick up the change
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      return Promise.reject(error);
    }

    // Skip if not a 401 or if it's the refresh endpoint itself
    if (error.response?.status !== 401 ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login') ||
        originalRequest._retry) {
      return Promise.reject(error);
    }

    // Check if we have a refresh token
    const refreshToken = localStorage.getItem("foundry-refresh-token");
    if (!refreshToken) {
      return Promise.reject(error);
    }

    if (isRefreshingGlobal) {
      // Queue this request to retry after refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return axios(originalRequest);
      }).catch(err => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshingGlobal = true;

    try {
      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: refreshToken,
      });

      if (response.data?.access_token) {
        const newToken = response.data.access_token;
        localStorage.setItem("foundry-token", newToken);
        if (response.data.refresh_token) {
          localStorage.setItem("foundry-refresh-token", response.data.refresh_token);
        }
        if (response.data.expires_at) {
          localStorage.setItem("foundry-expires-at", response.data.expires_at.toString());
        }

        // Update the failed request's auth header and retry
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        processQueue(null, newToken);

        console.log("[Auth Interceptor] Token refreshed, retrying request");
        return axios(originalRequest);
      }
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Clear auth state on refresh failure and force login
      localStorage.removeItem("foundry-token");
      localStorage.removeItem("foundry-refresh-token");
      localStorage.removeItem("foundry-expires-at");
      console.error("[Auth Interceptor] Token refresh failed, redirecting to login");
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshingGlobal = false;
    }

    return Promise.reject(error);
  }
);
